import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useParams } from "react-router-dom";
import { getBookingsForChalet, getChaletById } from "../services/dataService";
import { AMENITIES } from "../services/mockData";
import { Booking, Chalet, DayPrices } from "../types";
import {
  ArrowLeft,
  Bath,
  Beef,
  Calendar as CalendarIcon,
  Car,
  Check,
  ChevronLeft,
  ChevronRight,
  Dog,
  Flower,
  Grid,
  Map,
  MapPin,
  Star,
  ThermometerSun,
  Tv,
  Users,
  Utensils,
  Wifi,
  X,
  FlameKindling,
  Warehouse,
  Flame,
  Dumbbell,
  Bike,
  Cat,
  TreePine,
  AirVent, 
  House, 
  Bed, 
  Beach,
  Volleyball,
  CircleStar,
  WashingMachine,
  WavesLadder,
  Kayak,
  Heater,
  Utensils,
  AirVent,
  Sailboat,
  CableCar,
  MountainSnow 
} from "lucide-react";

import {
  addDays,
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isBefore,
  isSameDay,
  isSameMonth,
  isWithinInterval,
  parseISO,
  startOfDay,
  startOfMonth,
  startOfWeek,
} from "date-fns";
import { enUS, nl } from "date-fns/locale";
import { useLanguage } from "../context/LanguageContext";

export const ChaletDetail: React.FC = () => {
  const { t, language } = useLanguage();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [chalet, setChalet] = useState<Chalet | undefined>(undefined);
  const [guestCount, setGuestCount] = useState<number>(1);
  const [loading, setLoading] = useState(true);
  const [bookings, setBookings] = useState<Booking[]>([]);

  // Booking state
  const [selectedStartDate, setSelectedStartDate] = useState<Date | null>(null);
  const [selectedEndDate, setSelectedEndDate] = useState<Date | null>(null);
  const [currentMonthOffset, setCurrentMonthOffset] = useState(0);

// Min stay UI
const [minStayError, setMinStayError] = useState<string | null>(null);

// Required min nights based on check-in date only (Option 1)
const getRequiredMinNights = (start: Date): number => {
  if (!chalet) return 1;

  const base = Math.max(1, Number(chalet.minNights ?? 1));
  const iso = format(start, "yyyy-MM-dd");

  const periods = Array.isArray(chalet.minStayPeriods) ? chalet.minStayPeriods : [];
  for (const p of periods) {
    const s = p?.startDate;
    const e = p?.endDate;
    const mn = Math.max(1, Number(p?.minNights ?? 1));
    if (s && e && iso >= s && iso <= e) return mn;
  }

  return base;
};

const requiredMinNights = useMemo(() => {
  if (!selectedStartDate) return Math.max(1, Number(chalet?.minNights ?? 1));
  return getRequiredMinNights(selectedStartDate);
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [selectedStartDate, chalet]);


  // Gallery state
  const [isGalleryOpen, setIsGalleryOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const dateLocale = language === "nl" ? nl : enUS;

  useEffect(() => {
    let cancelled = false;

    (async () => {
      if (!id) return;

      try {
        setLoading(true);

        const c = await getChaletById(id);
        if (cancelled) return;

        if (c && (c as any).isActive === 0) {
          setChalet(undefined);
          setBookings([]);
          return;
        }

        setChalet(c);
		

        if (c) {
          const b = await getBookingsForChalet(c.id);
          if (cancelled) return;
          setBookings(b);
        } else {
          setBookings([]);
        }
      } catch (e) {
        console.error("ChaletDetail load failed:", e);
        if (!cancelled) {
          setChalet(undefined);
          setBookings([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [id]);

  useEffect(() => {
    document.body.style.overflow = isGalleryOpen ? "hidden" : "unset";
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isGalleryOpen]);
  
  useEffect(() => {
  const max = Math.max(1, Math.floor(Number((chalet as any)?.maxGuests ?? 1)));
  setGuestCount((prev) => Math.min(Math.max(1, Math.floor(Number(prev || 1))), max));
}, [chalet]);

  // Icons helper
  const getIcon = (id: string) => {
    const iconName = AMENITIES.find((a) => a.id === id)?.icon;
    switch (iconName) {
      case "Wifi":
        return <Wifi size={18} />;
      case "ThermometerSun":
        return <ThermometerSun size={18} />;
      case "Dog":
        return <Dog size={18} />;
      case "Map":
        return <Map size={18} />;
      case "Utensils":
        return <Utensils size={18} />;
      case "Car":
        return <Car size={18} />;
      case "Flower":
        return <Flower size={18} />;
      case "Bath":
        return <Bath size={18} />;
      case "Beef":
        return <Beef size={18} />;
      case "Tv":
        return <Tv size={18} />;
      case "Cat":
        return <Cat size={18} />;
      case "Bike":
        return <Bike size={18} />;
      case "Dumbbell":
        return <Dumbbell size={18} />;
      case "Flame":
        return <Flame size={18} />;
      case "Warehouse":
        return <Warehouse size={18} />;
      case "FlameKindling":
        return <FlameKindling size={18} />;
      case "TreePine":
        return <TreePine size={18} />;
      case "Volleyball":
        return <Volleyball size={18} />;
      case "House":
        return <House size={18} />;
      case "Bed":
        return <Bed size={18} />;
      case "AirVent":
        return <AirVent size={18} />;
      case "Heater":
        return <Heater size={18} />;
      case "Kayak":
        return <Kayak size={18} />;
      case "WavesLadder":
        return <WavesLadder size={18} />;
      case "WashingMachine":
        return <WashingMachine size={18} />;
      case "CircleStar":
        return <CircleStar size={18} />;
      case "Sailboat":
        return <Sailboat size={18} />;
      case "CableCar":
        return <CableCar size={18} />;
      case "MountainSnow":
        return <MountainSnow size={18} />;
      default:
        return <Check size={18} />;
    }
  };

  const getLabel = (id: string) => t(`amenity.${id}`);

  // ✅ Today (memoized so it doesn’t recreate every render)
  const today = useMemo(() => startOfDay(new Date()), []);

  const viewDate = useMemo(() => {
    return addMonths(startOfMonth(today), currentMonthOffset);
  }, [currentMonthOffset, today]);

  const calendarDays = useMemo(() => {
    const monthStart = startOfMonth(viewDate);
    const monthEnd = endOfMonth(monthStart);
    const startDate = startOfWeek(monthStart, { weekStartsOn: 1 });
    const endDate = endOfWeek(monthEnd, { weekStartsOn: 1 });
    return eachDayOfInterval({ start: startDate, end: endDate });
  }, [viewDate]);

  // ✅ Guards added so these can run safely even while chalet is not loaded yet
	const isWithinIntervalEndExclusive = (day: Date, start: Date, end: Date) => {
	  const d = startOfDay(day).getTime();
	  const s = startOfDay(start).getTime();
	  const e = startOfDay(end).getTime();
	  return d >= s && d < e; // ✅ end is exclusive
	};  
  
  const isDateBooked = (date: Date) => {
    if (!chalet) return false;

	  const booked = bookings.some((b) => {
		if (b.status === "cancelled") return false; // ✅ ignore cancelled
			return isWithinIntervalEndExclusive(
			  date,
			  parseISO(b.startDate),
			  parseISO(b.endDate)
			);
	  });
    if (booked) return true;

    if (chalet.unavailablePeriods) {
		return chalet.unavailablePeriods.some((p) =>
		  isWithinIntervalEndExclusive(date, parseISO(p.startDate), parseISO(p.endDate))
		);
    }

    return false;
  };

  const getPriceForDate = (date: Date): number => {
    if (!chalet) return 0;

    if (chalet.customPricePeriods) {
      const customPeriod = chalet.customPricePeriods.find((p) =>
        isWithinInterval(date, {
          start: parseISO(p.startDate),
          end: parseISO(p.endDate),
        })
      );
      if (customPeriod) return customPeriod.price;
    }

    if (chalet.dayPrices) {
      const dayName = format(date, "EEEE").toLowerCase() as keyof DayPrices;
      const dayPrice = chalet.dayPrices[dayName];
      if (dayPrice) return dayPrice;
    }

    return chalet.pricePerNight;
  };


  const handleDateClick = (date: Date) => {
  // ✅ Block expired/past days
  if (isBefore(date, today)) return;
  if (isDateBooked(date)) return;

  // Start selection (or restart selection)
  if (!selectedStartDate || (selectedStartDate && selectedEndDate)) {
    setSelectedStartDate(date);
    setSelectedEndDate(null);
    setMinStayError(null);
    return;
  }

  // Attempt to set end date
  if (date > selectedStartDate) {
    const nights = Math.ceil(
      (date.getTime() - selectedStartDate.getTime()) / (1000 * 3600 * 24)
    );

    const required = getRequiredMinNights(selectedStartDate);

    // ✅ Enforce minimum nights (Option 1: based on check-in date)
    if (nights < required) {
      setMinStayError(
        language === "nl"
          ? `Minimaal verblijf: ${required} nachten.`
          : `Minimum stay: ${required} nights.`
      );
      return;
    }

    let valid = true;
    for (let i = 1; i < nights; i++) {
      const d = addDays(selectedStartDate, i);
      if (isBefore(d, today) || isDateBooked(d)) valid = false;
    }

    if (valid) {
      setSelectedEndDate(date);
      setMinStayError(null);
    } else {
      setSelectedStartDate(date);
      setSelectedEndDate(null);
      setMinStayError(null);
    }
    return;
  }

  // Clicking same day or earlier resets start
  setSelectedStartDate(date);
  setSelectedEndDate(null);
  setMinStayError(null);
};

  const getSelectionState = (date: Date) => {
    const isStart = !!selectedStartDate && isSameDay(date, selectedStartDate);
    const isEnd = !!selectedEndDate && isSameDay(date, selectedEndDate);
    const inRange =
      !!selectedStartDate &&
      !!selectedEndDate &&
      isWithinInterval(date, { start: selectedStartDate, end: selectedEndDate });

    return { isStart, isEnd, inRange };
  };

  let totalPrice = 0;
  let totalNights = 0;

  if (selectedStartDate && selectedEndDate) {
    const interval = eachDayOfInterval({
      start: selectedStartDate,
      end: addDays(selectedEndDate, -1),
    });
    totalNights = interval.length;
    totalPrice = interval.reduce((sum, d) => sum + getPriceForDate(d), 0);
  }


  // ✅ Min stay validity (keeps the button disabled if too short)
  const isMinStayValid = !selectedStartDate || !selectedEndDate || totalNights >= requiredMinNights;

  const handleBookNow = () => {
    if (selectedStartDate && selectedEndDate && chalet) {
      navigate("/checkout", {
        state: {
          chalet,
          startDate: selectedStartDate,
          endDate: selectedEndDate,
		  guestCount,
          totalPrice,
        },
      });
    }
  };

  const openGallery = (index: number) => {
    setCurrentImageIndex(index);
    setIsGalleryOpen(true);
  };

  const nextImage = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!chalet?.images?.length) return;
    setCurrentImageIndex((prev) => (prev + 1) % chalet.images.length);
  };

  const prevImage = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!chalet?.images?.length) return;
    setCurrentImageIndex((prev) => (prev - 1 + chalet.images.length) % chalet.images.length);
  };

  // ---- RENDER ----
  if (loading) {
    return (
      <div className="h-screen flex flex-col items-center justify-center gap-4">
        <div className="w-12 h-12 border-4 border-brand-200 border-t-brand-900 rounded-full animate-spin" />
        <p className="font-medium text-stone-500">{t("detail.loading") || "Laden..."}</p>
      </div>
    );
  }

  if (!chalet) {
    return (
      <div className="h-screen flex flex-col items-center justify-center gap-6">
        <div className="bg-brand-50 p-6 rounded-full">
          <MapPin size={48} className="text-brand-300" />
        </div>
        <h2 className="text-2xl font-serif font-bold text-brand-900">Verblijf niet gevonden</h2>
        <button
          onClick={() => navigate("/overview")}
          className="px-6 py-2 bg-brand-900 text-white rounded-xl font-bold"
        >
          {t("detail.back")}
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 animate-in fade-in duration-500">
      {/* Lightbox Gallery Modal */}
      {isGalleryOpen &&
        createPortal(
          <div
          className="fixed inset-0 z-[9999] bg-black/95 flex flex-col items-center justify-center animate-in fade-in duration-200"
          onClick={() => setIsGalleryOpen(false)}
        >
          <button
            className="absolute top-6 left-6 text-white/70 hover:text-white p-2"
            onClick={() => setIsGalleryOpen(false)}
          >
            <X size={32} />
          </button>

          <div className="absolute top-6 right-6 text-white/70 font-medium">
            {currentImageIndex + 1} / {chalet.images.length}
          </div>

          <div className="relative w-full h-full max-w-6xl max-h-[85vh] flex items-center justify-center px-12">
            <button
              onClick={prevImage}
              className="absolute left-2 md:left-4 p-3 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
            >
              <ChevronLeft size={32} />
            </button>

            <img
              src={chalet.images[currentImageIndex]}
              alt="Gallery Fullscreen"
              className="max-h-full max-w-full object-contain shadow-2xl rounded-sm"
              onClick={(e) => e.stopPropagation()}
            />

            <button
              onClick={nextImage}
              className="absolute right-2 md:right-4 p-3 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
            >
              <ChevronRight size={32} />
            </button>
          </div>

          <div className="absolute bottom-6 flex gap-2 overflow-x-auto max-w-full px-4 scrollbar-hide">
            {chalet.images.map((img, idx) => (
              <button
                key={idx}
                onClick={(e) => {
                  e.stopPropagation();
                  setCurrentImageIndex(idx);
                }}
                className={`w-16 h-16 rounded-lg overflow-hidden border-2 transition-all flex-shrink-0 ${
                  idx === currentImageIndex
                    ? "border-white opacity-100"
                    : "border-transparent opacity-50 hover:opacity-80"
                }`}
              >
                <img src={img} className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        </div>
          ,
          document.body
        )}

      <button onClick={() => navigate(-1)} className="flex items-center text-brand-600 mb-6 hover:underline font-medium">
        <ArrowLeft size={20} className="mr-2" /> {t("detail.back")}
      </button>

      {/* Header */}
      <div className="mb-8">
        <h1 className="text-4xl font-serif font-bold text-brand-900 mb-2">{chalet.title}</h1>
        <div className="flex items-center text-stone-600 gap-4 text-sm">
          <span className="flex items-center gap-1">
            <MapPin size={16} /> {chalet.location}, {chalet.province}
          </span>
          <span className="flex items-center gap-1">
            <Users size={16} /> {chalet.maxGuests} {t("detail.guests")}
          </span>
		  {/*
          <span className="flex items-center gap-1 text-brand-600 font-semibold">
            <Star size={16} fill="currentColor" /> {chalet.rating} (12 {t("detail.reviews")})
          </span>*/}
        </div>
      </div>

      {/* Interactive Images Grid */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-12 h-[500px] rounded-[2rem] overflow-hidden shadow-lg relative isolate">
        <div className="md:col-span-2 md:row-span-2 h-full cursor-pointer overflow-hidden" onClick={() => openGallery(0)}>
          <img src={chalet.images[0]} className="relative z-0 w-full h-full object-cover hover:scale-105 transition-transform duration-700" alt="Main" />
        </div>
        <div className="hidden md:block h-full cursor-pointer overflow-hidden" onClick={() => openGallery(1)}>
          <img src={chalet.images[1] || chalet.images[0]} className="relative z-0 w-full h-full object-cover hover:scale-105 transition-transform duration-700" alt="Sub 1" />
        </div>
        <div className="hidden md:block h-full cursor-pointer overflow-hidden" onClick={() => openGallery(2)}>
          <img src={chalet.images[2] || chalet.images[0]} className="relative z-0 w-full h-full object-cover hover:scale-105 transition-transform duration-700" alt="Sub 2" />
        </div>
        <div className="hidden md:block h-full cursor-pointer overflow-hidden" onClick={() => openGallery(3)}>
          <img src={chalet.images[3] || chalet.images[0]} className="relative z-0 w-full h-full object-cover hover:scale-105 transition-transform duration-700 opacity-80" alt="Sub 3" />
        </div>
<div
  className="hidden md:block h-full relative group cursor-pointer overflow-hidden"
  onClick={() => openGallery(4)}
>
  <img
    src={chalet.images[4] || chalet.images[0]}
    className="relative z-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
    alt="Sub 4"
  />
  <div className="absolute inset-0 z-10 bg-black/30 flex items-center justify-center text-white font-medium group-hover:bg-black/40 transition-colors">
    <Grid className="mr-2" size={18} /> {chalet.images.length} foto's
  </div>
</div>

<button
  onClick={() => openGallery(0)}
  className="absolute bottom-4 right-4 z-20 bg-white px-4 py-2 rounded-lg shadow-md text-sm font-bold flex items-center gap-2 md:hidden"
>
  <Grid size={16} /> {chalet.images.length} foto's
</button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
        {/* Left Column: Info */}
        <div className="lg:col-span-2 space-y-10">
          <div className="prose prose-stone max-w-none">
            <h3 className="text-2xl font-serif font-bold text-brand-900 mb-4">{t("detail.about")}</h3>
            <p className="text-stone-600 leading-relaxed text-lg">{chalet.description}</p>
          </div>

          <div className="border-t border-brand-100 pt-8">
            <h3 className="text-xl font-serif font-bold text-brand-900 mb-6">{t("detail.amenities")}</h3>
            <div className="grid grid-cols-2 gap-4">
              {chalet.amenities.map((amId) => (
                <div key={amId} className="flex items-center gap-3 text-stone-700 p-3 bg-brand-50/50 rounded-xl">
                  <span className="text-brand-600">{getIcon(amId)}</span>
                  <span>{getLabel(amId)}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="border-t border-brand-100 pt-8">
            <h3 className="text-xl font-serif font-bold text-brand-900 mb-6">{t("detail.reviews")}</h3>
            <div className="space-y-6">
              {chalet.reviews.length > 0 ? (
                chalet.reviews.map((rev) => (
                  <div key={rev.id} className="bg-white p-6 rounded-2xl shadow-sm border border-brand-50">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-8 h-8 rounded-full bg-brand-200 flex items-center justify-center text-brand-800 font-bold text-xs">
                        {rev.user.charAt(0)}
                      </div>
                      <span className="font-semibold text-stone-900">{rev.user}</span>
                      <span className="text-stone-400 text-sm">• {rev.date}</span>
                    </div>
                    <p className="text-stone-600 italic">"{rev.comment}"</p>
                  </div>
                ))
              ) : (
                <p className="text-stone-500">Nog geen reviews.</p>
              )}
            </div>
          </div>
        </div>

        {/* Right Column: Booking Card */}
        <div className="relative">
          <div className="top-24 bg-white rounded-[2rem] shadow-xl border border-brand-100 p-8">
            <div className="flex justify-between items-baseline mb-6">
              <div>
                <span className="text-3xl font-serif font-bold text-brand-900">€{chalet.pricePerNight}</span>
                <span className="text-stone-500 text-sm"> / {t("card.night")}</span>
              </div>
            </div>

            <div className="bg-brand-50 rounded-2xl p-4 mb-6">
              <div className="flex items-center justify-between mb-4">
                <span className="text-sm font-semibold text-brand-900 uppercase tracking-wide">{t("detail.checkAvailability")}</span>
                <CalendarIcon size={16} className="text-brand-600" />
              </div>

              <div className="flex items-center justify-between mb-6 bg-white rounded-2xl p-2.5 shadow-sm border border-brand-100">
                <button
                  onClick={() => setCurrentMonthOffset((p) => Math.max(0, p - 1))}
                  disabled={currentMonthOffset === 0}
                  className={`p-1.5 rounded-xl transition-all ${
                    currentMonthOffset === 0 ? "text-stone-300" : "text-brand-900 hover:bg-brand-50"
                  }`}
                >
                  <ChevronLeft size={20} />
                </button>

                <span className="text-xs font-bold text-brand-900 uppercase tracking-widest">
                  {format(viewDate, "MMMM yyyy", { locale: dateLocale })}
                </span>

                <button
                  onClick={() => setCurrentMonthOffset((p) => p + 1)}
                  className="p-1.5 rounded-xl text-brand-900 hover:bg-brand-50 transition-all"
                >
                  <ChevronRight size={20} />
                </button>
              </div>

              {/* Weekdays */}
              <div className="grid grid-cols-7 gap-0 text-center text-sm mb-2">
                {["MA", "DI", "WO", "DO", "VR", "ZA", "ZO"].map((d, i) => (
                  <span key={i} className="text-stone-400 text-xs font-bold pb-2">
                    {d}
                  </span>
                ))}
              </div>

              {/* Calendar days */}
              <div className="grid grid-cols-7 gap-0 gap-y-1 text-center">
                {calendarDays.map((date, idx) => {
                  const booked = isDateBooked(date);
                  const isPast = isBefore(date, today);

                  const { isStart, isEnd, inRange } = getSelectionState(date);
                  const price = getPriceForDate(date);

                  let buttonClass =
                    "w-10 h-10 flex items-center justify-center text-sm relative transition-all duration-300";
                  let innerClass = "w-10 h-10 flex items-center justify-center rounded-full";

                  // Disabled state (booked OR past)
                  if (booked || isPast) {
                    buttonClass += " text-stone-300 cursor-not-allowed bg-stone-100";
                  } else {
                    buttonClass += " cursor-pointer animate-fade-in";
                  }

                  // Selection/hover styles only if selectable
                  if (!booked && !isPast) {
                    if (isStart) {
                      buttonClass += " bg-brand-900 text-white rounded-l-full z-10";
                    } else if (isEnd) {
                      buttonClass += " bg-brand-900 text-white rounded-r-full z-10";
                    } else if (inRange) {
                      buttonClass += " bg-gradient-to-r from-brand-100 to-brand-200 text-brand-900";
                      innerClass = "w-full h-full flex items-center justify-center";
                    } else {
                      buttonClass += " text-stone-700 hover:bg-brand-100 hover:rounded-full";
                    }
                  }

                  return (
                    <button
                      key={idx}
                      disabled={booked || isPast}
                      onClick={() => handleDateClick(date)}
                      title={booked ? "Niet beschikbaar" : isPast ? "Verlopen datum" : `€${price}`}
                      className={buttonClass}
                      style={{
                        animationDelay: `${idx * 15}ms`,
                        backgroundImage: booked
                          ? "linear-gradient(45deg, #f5f5f4 25%, #e7e5e4 25%, #e7e5e4 50%, #f5f5f4 50%, #f5f5f4 75%, #e7e5e4 75%, #e7e5e4 100%)"
                          : isPast
                          ? "linear-gradient(45deg, #fafaf9 25%, #f5f5f4 25%, #f5f5f4 50%, #fafaf9 50%, #fafaf9 75%, #f5f5f4 75%, #f5f5f4 100%)"
                          : undefined,
                        backgroundSize: booked || isPast ? "10px 10px" : undefined,
                      }}
                    >
                      <span className={innerClass}>{format(date, "d")}</span>

                    </button>
                  );
                })}
              </div>
			  



              {/* ✅ Minimum stay info + validation */}
              {minStayError && (
                <div className="mt-3 p-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm font-medium">
                  {minStayError}
                </div>
              )}
            </div>
			
{/* Guests selector (under calendar) */}
<div className="mt-4 mb-6">
  <label className="block text-sm font-medium text-stone-700 mb-2">
    Aantal gasten
  </label>

  <div className="flex items-center gap-2">
    <button
      type="button"
      onClick={() => setGuestCount((g) => Math.max(1, g - 1))}
      className="h-10 w-10 rounded-lg border border-stone-300 bg-white text-lg"
      aria-label="Minder gasten"
    >
      −
    </button>

    <select
      className="h-10 flex-1 rounded-lg border border-stone-300 bg-white px-3"
      value={guestCount}
      onChange={(e) => {
        const max = Math.max(1, Math.floor(Number((chalet as any)?.maxGuests ?? 1)));
        const val = Math.floor(Number(e.target.value));
        setGuestCount(Math.min(Math.max(1, val), max));
      }}
    >
      {Array.from(
        { length: Math.max(1, Math.floor(Number((chalet as any)?.maxGuests ?? 1))) },
        (_, i) => i + 1
      ).map((n) => (
        <option key={n} value={n}>
          {n} {n === 1 ? "gast" : "gasten"}
        </option>
      ))}
    </select>

    <button
      type="button"
      onClick={() => {
        const max = Math.max(1, Math.floor(Number((chalet as any)?.maxGuests ?? 1)));
        setGuestCount((g) => Math.min(max, g + 1));
      }}
      className="h-10 w-10 rounded-lg border border-stone-300 bg-white text-lg"
      aria-label="Meer gasten"
    >
      +
    </button>
  </div>

  <p className="mt-2 text-xs text-stone-500">
    Maximaal {Math.max(1, Math.floor(Number((chalet as any)?.maxGuests ?? 1)))} gasten
  </p>
</div>

            {selectedStartDate && selectedEndDate && (
              <div className="space-y-3 mb-6 p-4 border border-brand-100 rounded-xl bg-white">
				<div className="flex justify-between text-sm">
				  <span className="text-stone-600 flex items-center gap-2">
					<CalendarIcon size={16} className="text-stone-400" />
					{format(selectedStartDate, "dd MMM yyyy", { locale: dateLocale })} –{" "}
					{format(selectedEndDate, "dd MMM yyyy", { locale: dateLocale })}
				  </span>
				</div>
                <div className="flex justify-between text-sm">
                  <span className="text-stone-600">
                    {t("checkout.stay")} ({totalNights} {t("checkout.nights")})
                  </span>
                  <span className="font-medium">€{totalPrice}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-stone-600">{t("detail.cleaning")}</span>
                  <span className="font-medium">€{chalet.cleaningFee}</span>
                </div>
                <div className="border-t border-brand-100 pt-2 flex justify-between font-bold text-brand-900">
                  <span>{t("detail.total")}</span>
                  <span>€{totalPrice + chalet.cleaningFee}</span>
                </div>
              </div>
            )}

            <button
              onClick={handleBookNow}
              disabled={!selectedStartDate || !selectedEndDate || !isMinStayValid}
              className={`w-full py-4 rounded-xl font-bold text-white transition-all shadow-lg transform active:scale-95 ${
                !selectedStartDate || !selectedEndDate
                  ? "bg-stone-300 cursor-not-allowed shadow-none"
                  : "bg-brand-900 hover:bg-black shadow-brand-200"
              }`}
            >
              {!selectedStartDate || !selectedEndDate
                ? t("detail.chooseDates")
                : !isMinStayValid
                ? (language === "nl"
                    ? `Min. ${requiredMinNights} nachten`
                    : `Min. ${requiredMinNights} nights`)
                : t("detail.bookNow")}
            </button>

            <p className="text-center text-xs text-stone-400 mt-4 flex items-center justify-center gap-1">
              <ShieldCheckIcon /> Je betaalt nog niets
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

const ShieldCheckIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
  </svg>
);